import { Controller, useFormContext } from 'react-hook-form';
import Select from 'react-select';
import { inputStyles } from '~/components/Vendors/styles';
import { toUSD } from '~/utils';
import UploadDocument from './UploadDocuments';
import { useEffect, useState, useCallback } from 'react';
import {
    FundRequestExpenseTypes,
    fundRequestExpenseTypesList,
} from '~/enums/fundRequest';
import { useFundRequestContext } from '~/context/FundRequestProvider';
import { SelectOption } from '~/types/globals';

interface FoodExpenseFormProps {
    onDocumentClick?: (slideIndex: number) => void;
    selectedDocumentIndex?: number;
}

const FoodExpenseForm = ({ onDocumentClick, selectedDocumentIndex }: FoodExpenseFormProps) => {
    const { formKey, annualRates, glCodes } = useFundRequestContext();
    const { control, register, watch, getValues, setValue, formState: { errors } } = useFormContext();
    const vals = watch(formKey);
    const [totalDays, setTotalDays] = useState(0);
    const [currentPerDiemRate, setCurrentPerDiemRate] = useState(0);

    const getTotalDays = (startDate: string, endDate: string) => {
        if (!startDate || !endDate) {
            return 0;
        }

        const start = new Date(startDate);
        const end = new Date(endDate);

        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
            return 0;
        }

        const differenceInTime = end.getTime() - start.getTime();
        if (differenceInTime < 0) {
            return 0;
        }

        // Add 1 to make the calculation inclusive (same day = 1 day)
        return Math.floor(differenceInTime / (1000 * 3600 * 24)) + 1;
    };

    const [start_date, end_date, recipient_type, override_rate_per_day] = watch([
        `${formKey}.start_date`,
        `${formKey}.end_date`,
        `${formKey}.recipient_type`,
        `${formKey}.override_rate_per_day`,
    ]);

    // Helper function to get the correct per_diem rate based on recipient_type
    const getPerDiemRate = useCallback((recipient_type: string) => {
        if (!annualRates?.per_diem) return 0;
        
        if (Array.isArray(annualRates.per_diem)) {
            // Find the rate that matches the recipient_type
            const matchingRate = annualRates.per_diem.find(
                (rate: { recipient_type: string; value: number }) => 
                    rate.recipient_type === recipient_type
            );
            return matchingRate?.value || 0;
        } else {
            // If it's a number, use it directly (legacy support)
            return annualRates.per_diem;
        }
    }, [annualRates]);

    // Update per diem rate when recipient type changes
    useEffect(() => {
        if (recipient_type) {
            const rate = getPerDiemRate(recipient_type);
            setCurrentPerDiemRate(rate);
        } else {
            setCurrentPerDiemRate(0);
        }
    }, [recipient_type, annualRates, getPerDiemRate]);

    useEffect(() => {
        const days = getTotalDays(start_date, end_date);
        setTotalDays(days);
    }, [formKey, start_date, end_date]);

    useEffect(() => {
        if (!formKey) return;
        
        // If override_rate_per_day is set, use it directly as the total amount
        if (override_rate_per_day !== null && override_rate_per_day !== undefined) {
            const currentAmount = Number(getValues(`${formKey}.amount`) ?? 0);
            if (currentAmount !== override_rate_per_day) {
                setValue(`${formKey}.amount`, override_rate_per_day);
            }
        } else {
            // Otherwise calculate from days * rate
            const targetAmount = totalDays > 0 && currentPerDiemRate > 0
                ? totalDays * currentPerDiemRate
                : 0;
            const currentAmount = Number(getValues(`${formKey}.amount`) ?? 0);

            if (currentAmount !== targetAmount) {
                setValue(`${formKey}.amount`, targetAmount);
            }
        }
    }, [totalDays, currentPerDiemRate, override_rate_per_day, formKey, setValue, getValues]);

    const currentItem = formKey ? getValues(formKey) : null;

    return (
        <div className='flex gap-4 divide-x divide-bodydark'>
            <div className='w-full'>
                <div className='mt-3 w-[100%]'>
                    <label
                        htmlFor='mid-primary-medical-reason'
                        className='block font-bold'
                    >
                        Select Category
                    </label>
                    <select
                        value={FundRequestExpenseTypes.FOOD}
                        disabled
                        id='mid-primary-medical-reason'
                        className={`${inputStyles} bg-grey w-full`}
                    >
                        <option value='' disabled>
                            Select Category
                        </option>
                        {fundRequestExpenseTypesList.map((t, i) => {
                            return (
                                <option value={t} key={i}>
                                    {t}
                                </option>
                            );
                        })}
                    </select>
                </div>
                <div className='mb-3 flex pt-[10px]'>
                    <div className='w-[100%]'>
                        <label
                            htmlFor={`${formKey}.recipient_type`}
                            className='block font-bold'
                        >
                            Client Type <span className='text-red'>*</span>
                        </label>
                        <select
                            {...register(`${formKey}.recipient_type`, {
                                required: 'Client Type is required',
                            })}
                            className={`${inputStyles} bg-grey w-full`}
                        >
                            <option value='' disabled>
                                Select
                            </option>
                            <option value='client'>Client</option>
                            <option value='caregiver'>Caregiver</option>
                            <option value='both'>Both</option>
                        </select>
                        {errors[formKey]?.recipient_type && (
                            <span className="text-red-500 text-sm mt-1 block">
                                {errors[formKey]?.recipient_type?.message}
                            </span>
                        )}
                    </div>
                </div>
                <div className='mb-3 flex-1'>
                    <label
                        htmlFor={`${formKey}.recipient_name`}
                        className='font-semibold'
                    >
                        Recipient Name <span className='text-red'>*</span>
                    </label>
                    <input
                        className={`w-[100%] rounded-md border-2 ${vals?.recipient_name ? 'border-black' : 'border-gray-300'} p-[10px]`}
                        {...register(`${formKey}.recipient_name`, {
                            required: true,
                            pattern: {
                                value: /^[^"]*$/,
                                message: 'Recipient name cannot contain double quote characters (")'
                            }
                        })}
                    />
                    {errors[formKey]?.recipient_name && (
                        <span className="text-red-500 text-sm mt-1 block">
                            {errors[formKey]?.recipient_name?.message}
                        </span>
                    )}
                </div>

                <div style={{ display: 'flex' }} className='mb-3 flex'>
                    <div className='flex-1'>
                        <label
                            htmlFor={`${formKey}.start_date`}
                            className='font-semibold'
                        >
                            Start Date <span className='text-red'>*</span>
                        </label>
                        <input
                            type='date'
                            className={`${inputStyles} w-full`}
                            {...register(`${formKey}.start_date`, {
                                required: 'Start Date is required',
                            })}
                        />
                        {errors[formKey]?.start_date && (
                            <span className="text-red-500 text-sm mt-1 block">
                                {errors[formKey]?.start_date?.message}
                            </span>
                        )}
                    </div>
                    <div className='mx-3 flex-1'>
                        <label
                            htmlFor={`${formKey}.end_date`}
                            className='font-semibold'
                        >
                            End Date <span className='text-red'>*</span>
                        </label>
                        <input
                            type='date'
                            className={`${inputStyles} w-full`}
                            {...register(`${formKey}.end_date`, {
                                required: 'End Date is required',
                            })}
                        />
                        {errors[formKey]?.end_date && (
                            <span className="text-red-500 text-sm mt-1 block">
                                {errors[formKey]?.end_date?.message}
                            </span>
                        )}
                    </div>
                    <div className='flex-1'>
                        <label className='font-bold'># of Days</label>
                        <p className='pt-3'>{totalDays}</p>
                    </div>
                </div>

                <div className='mb-4'>
                    <label className='font-bold'>Rate per Day</label>
                    <p className='pt-3'>
                        ${currentPerDiemRate}
                    </p>
                </div>

                <div className='mb-4'>
                    <label className='font-bold'>Calculated Total</label>
                    <p className={`pt-3 ${override_rate_per_day !== null && override_rate_per_day !== undefined ? 'line-through text-gray-500' : ''}`}>
                        {toUSD(totalDays * currentPerDiemRate)}
                    </p>
                </div>

                <div className='mb-4'>
                    <label className='flex items-center gap-2 font-bold cursor-pointer'>
                        <input
                            type='checkbox'
                            checked={override_rate_per_day !== null && override_rate_per_day !== undefined}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    // When checked, set override to calculated total as default
                                    setValue(`${formKey}.override_rate_per_day`, totalDays * currentPerDiemRate);
                                } else {
                                    // When unchecked, clear the override
                                    setValue(`${formKey}.override_rate_per_day`, null);
                                }
                            }}
                            className='w-4 h-4 cursor-pointer'
                        />
                        Override Total Amount
                    </label>
                    {override_rate_per_day !== null && override_rate_per_day !== undefined && (
                        <div className='mt-2 relative'>
                            <span className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 font-medium'>
                                $
                            </span>
                            <input
                                type='number'
                                step='0.01'
                                min='0'
                                className={`w-full rounded-md border-2 ${override_rate_per_day && override_rate_per_day > 0 ? 'border-black' : 'border-gray-300'} p-[10px] pl-7`}
                                {...register(`${formKey}.override_rate_per_day`, {
                                    valueAsNumber: true,
                                    min: {
                                        value: 0,
                                        message: 'Total amount must be a positive number'
                                    }
                                })}
                                placeholder='0.00'
                            />
                            {errors[formKey]?.override_rate_per_day && (
                                <span className="text-red-500 text-sm mt-1 block">
                                    {errors[formKey]?.override_rate_per_day?.message}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                <div className='mb-2'>
                    <label className='font-bold'>Total</label>
                    <p className='pt-3'>{toUSD(vals?.amount)}</p>
                </div>
                <UploadDocument
                    expenseItem={currentItem}
                    setSlideIndex={onDocumentClick}
                    selectedIndex={selectedDocumentIndex}
                />
                <div className='mt-5'>
                    <label htmlFor='total' className='block font-semibold'>
                        GL Code
                    </label>
                    <Controller
                        name={`${formKey}.gl_code`}
                        control={control}
                        render={({ field }) => (
                            <Select
                                {...field}
                                value={glCodes.filter(
                                    (option: SelectOption) =>
                                        option.value ===
                                        getValues(`${formKey}.gl_code`)
                                )}
                                onChange={(option) => {
                                    field.onChange(option?.value);
                                }}
                                options={glCodes}
                                formatOptionLabel={(option: SelectOption) => 
                                    `${option.value} - ${option.label}`
                                }
                            />
                        )}
                    />
                </div>
            </div>
        </div>
    );
};

export default FoodExpenseForm;
